import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  Card,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getAPICall, putAPICall } from "lib/callApi";
import { GET_HUB_SETTINGS } from "lib/constants";
import Loader from "utils/Loader";
import MDSnackbar from "components/MDSnackbar";
import { Form, Formik } from "formik";

const ReportsSettings = () => {
  const [pageLoading, setPageLoading] = useState(true);
  const [reportSettings, setReportSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const fetchReportSettings = async () => {
    setPageLoading(true);
    try {
      const fetchedSettings = await getAPICall(`${GET_HUB_SETTINGS}/reports`, true);
      if (fetchedSettings?.data?.success) {
        setReportSettings(fetchedSettings.data.settings);
      } else {
        setReportSettings({});
        if (fetchedSettings.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: fetchedSettings.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setPageLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const result = await putAPICall(`${GET_HUB_SETTINGS}/reports`, true, data);
      if (result.status === 200) {
        setShowSnackbar(true);
        fetchReportSettings();
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              result?.response?.data?.errorMessage ||
              "Something went wrong! Development has been notified.",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setSaving(false);
    }
  };

  const closeSnackbar = () => setShowSnackbar(false);

  useEffect(() => {
    fetchReportSettings();
  }, []);

  return (
    <Grid item xs={12}>
      <Loader loading={pageLoading} />
      <Card
        sx={{
          p: 3,
          borderRadius: "md",
          backgroundColor: "white",
          height: "100%",
        }}
      >
        <MDBox p={3} pb={0}>
          <MDTypography variant="h4" pb={4}>
            Reports
          </MDTypography>
        </MDBox>
        {!pageLoading && (
          <Formik enableReinitialize initialValues={reportSettings} onSubmit={onSubmit}>
            {({ values, setFieldValue, dirty, resetForm }) => (
              <Form>
                <Table aria-label="simple table">
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          borderBottom: "none",
                        }}
                      >
                        <FormGroup>
                          <FormControlLabel
                            control={<Switch checked={values.daily.active} />}
                            onChange={(e) => setFieldValue("daily.active", e.target.checked)}
                            label="Daily Reports"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormControlLabel
                            control={<Switch checked={values.daily.mediums.app} />}
                            onChange={(e) => setFieldValue("daily.mediums.app", e.target.checked)}
                            label="In App"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                          <FormControlLabel
                            control={<Switch checked={values.daily.mediums.email} />}
                            onChange={(e) => setFieldValue("daily.mediums.email", e.target.checked)}
                            label="Email"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </FormGroup>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          borderBottom: "none",
                        }}
                      >
                        <FormGroup>
                          <FormControlLabel
                            control={<Switch checked={values.weekly.active} />}
                            onChange={(e) => setFieldValue("weekly.active", e.target.checked)}
                            label="Weekly Reports"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormControlLabel
                            control={<Switch checked={values.weekly.mediums.app} />}
                            onChange={(e) => setFieldValue("weekly.mediums.app", e.target.checked)}
                            label="In App"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                          <FormControlLabel
                            control={<Switch checked={values.weekly.mediums.email} />}
                            onChange={(e) =>
                              setFieldValue("weekly.mediums.email", e.target.checked)
                            }
                            label="Email"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </FormGroup>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          borderBottom: "none",
                        }}
                      >
                        <FormGroup>
                          <FormControlLabel
                            control={<Switch checked={values.monthly.active} />}
                            onChange={(e) => setFieldValue("monthly.active", e.target.checked)}
                            label="Monthly Reports"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormControlLabel
                            control={<Switch checked={values.monthly.mediums.app} />}
                            onChange={(e) => setFieldValue("monthly.mediums.app", e.target.checked)}
                            label="In App"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                          <FormControlLabel
                            control={<Switch checked={values.monthly.mediums.email} />}
                            onChange={(e) =>
                              setFieldValue("monthly.mediums.email", e.target.checked)
                            }
                            label="Email"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </FormGroup>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <MDBox sx={{ display: "flex", justifyContent: "end", mt: 1 }}>
                  <MDButton
                    color="gray80"
                    size="small"
                    onClick={resetForm}
                    sx={{ mt: 1, mr: 1, position: "relative" }}
                    disabled={saving || !dirty}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    color="primaryRed"
                    size="small"
                    type="submit"
                    sx={{ mt: 1, position: "relative" }}
                    disabled={saving || !dirty}
                  >
                    {saving ? "Saving..." : "Save"}
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>
        )}
      </Card>
      <MDSnackbar
        color="success"
        icon="checkCircleIcon"
        title="Success"
        content="Reports settings saved successfully"
        open={showSnackbar}
        onClose={closeSnackbar}
        close={closeSnackbar}
        bgWhite
      />
    </Grid>
  );
};

export default ReportsSettings;
